import React, { useState } from "react";

import { Card, CardBody } from "reactstrap";

import CardHeader from "../Layout/CardHeader";
import SuperInsightsModal from "../Ia/SuperInsightsModal";
import Meta from '../Layout/Meta';

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// Padrões visuais (alinhados com BarChartX)
const FONT_FAMILY = "Poppins, sans-serif";
const TICK_FONT_SIZE = 9;
const TICK_FONT_WEIGHT_X = 600; // X mais forte
const TICK_FONT_WEIGHT_Y = 400; // Y um pouco mais leve
const TICK_COLOR_X = "#000";
const TICK_COLOR_Y = "#666";

// Tooltip customizado para padronizar fontes e estilos
const CustomTooltip = ({ active, label, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{
      background: "#FFF",
      border: "1px solid #ccc",
      borderRadius: 5,
      padding: 10,
      color: "#000",
      boxShadow: "0 2px 6px rgba(0,0,0,0.05)"
    }}>
      <div style={{
        fontFamily: FONT_FAMILY,
        fontSize: 12,
        fontWeight: 700,
        color: "#666",
        marginBottom: 6
      }}>{label}</div>
      {payload.map((entry, idx) => (
        <div key={idx} style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontFamily: FONT_FAMILY,
          fontSize: 9,
          fontWeight: 600
        }}>
          <span style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: 5,
            background: entry.color
          }} />
          <span>{`${entry.name}: ${Number(entry.value ?? 0).toLocaleString('pt-BR')}`}</span>
        </div>
      ))}
    </div>
  );
};


// Configuração dinâmica para os eixos Y
// Estilos de ticks
const axisTickStyleX = {
  fill:   TICK_COLOR_X,
  fontFamily: FONT_FAMILY,
  fontSize: TICK_FONT_SIZE,
  fontWeight: TICK_FONT_WEIGHT_X,
};

const axisTickStyleY = {
  fill: TICK_COLOR_Y,
  fontFamily: FONT_FAMILY,
  fontSize: TICK_FONT_SIZE,
  fontWeight: TICK_FONT_WEIGHT_Y,
};

const yAxes = [
  {
    yAxisId: "left",
    // Exemplo de formatação do valor
    tickFormatter: (value) => value,
    tick: axisTickStyleY,
    axisLine: false,
    tickLine: false,
  },
  {
    yAxisId: "right",
    orientation: "right",
    tick: axisTickStyleY,
    axisLine: false,
    tickLine: false,
  },
];


const ComposedCharts = ({ item }) => {

  // Verificação segura do item e seus valores
  const safeItem = item || {};

  // Destructuring com fallbacks seguros
  let {
    Sigla = '',
    Descricao_Dashboard_Componente: titulo = "-",
    dadosComponente = {},
    Valor_Meta = {},
    Texto_Info = "-",
    Texto_BizAI = "",
    ESG_Info = "-",
    Codigo_Transacao_Link = "",
    Titulo_Transacao_Link = "",
    opcoesComponente,
  } = safeItem;

  // Controle do Modal
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const togglePopup = () => setIsPopupOpen(!isPopupOpen);
  const slugId = Sigla.trim().replace(/\s+/g, "-");
  
  // Estado de dados
  const series = Array.isArray(opcoesComponente) ? opcoesComponente : [];
  const hasData = Array.isArray(dadosComponente) ? dadosComponente.length > 0 : false;
  const hasSeries = series.length > 0;
  const showChart = hasData && hasSeries;
  
  return (
    <>
      <Card className="card-dash-item">
         <CardBody className="d-flex flex-column cardbody-dash">
    
         <CardHeader
              titulo={titulo}
              Texto_Info={Texto_Info}
              ESG_Info={ESG_Info}
              togglePopup={togglePopup}
              slugId={slugId}
              textoIA={Texto_BizAI}
              Codigo_Transacao_Link={Codigo_Transacao_Link}
              Titulo_Transacao_Link={Titulo_Transacao_Link}
          />

          {/* Metas e Valores */}       
          <Meta Valor_Meta={Valor_Meta} />


          <div
            className={`d-flex ${showChart ? '' : 'align-items-center justify-content-center'}`}
            style={{
              marginTop: "20px",
              width: "100%",
              height: "300px" ,
            }}
          >
          {showChart ? (
            <ResponsiveContainer>
              <ComposedChart
                data={dadosComponente}
                margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#ccc" vertical={false} />
                {/* Eixo X com nome dos meses */}
                <XAxis dataKey="name" tick={axisTickStyleX} />
                
                {/* Renderizamos dinamicamente os eixos Y */}
                {yAxes.map((axis, index) => (
                  <YAxis key={index} {...axis} />
                ))}

                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ fontFamily: FONT_FAMILY, fontSize: 12, color: "#000" }}
                  formatter={(value) => (
                    <span style={{ color: "#000", fontSize: "12px", fontFamily: FONT_FAMILY, fontWeight: 400 }}>{value}</span>
                  )}
                />

                {/* Renderizamos dinamicamente cada série */}
                {series.map((item, index) => {
                  switch (item.type) {
                    case "Bar":
                      return <Bar key={index} {...item.props} />;
                    case "Line":
                      return <Line key={index} {...item.props} />;
                    default:
                      return null;
                  }
                })}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="font-size-14 w-100 h-100 d-flex vertical-center align-items-center justify-content-center text-muted">
              Sem dados para exibir
            </div>
          )}
        </div>      
      </CardBody>
    </Card>

      {/* Modal de Insights */}
      <SuperInsightsModal
        isOpen={isPopupOpen}
        toggle={togglePopup}
        textoIA={Texto_BizAI} // Passamos somente o texto da pergunta
      />
    </>
  );
};

export default ComposedCharts;
